/** The filter row. Every control writes straight into the URL. */

import * as Dialog from "@radix-ui/react-dialog";
import * as Popover from "@radix-ui/react-popover";
import { useEffect, useRef, useState } from "react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  activeFilterCount,
  datePreset,
  EMPTY_FILTERS,
  TYPE_LABELS,
  type LedgerFilters,
  type TxnTypeFilter,
} from "../../lib/filters";
import { formatAmount, parseAmount } from "../../lib/money";
import { cn } from "../../lib/utils";
import type { Member } from "../../lib/api";

interface Props {
  filters: LedgerFilters;
  onChange: (next: LedgerFilters) => void;
  members: Member[];
  categories: { id: number; name: string }[];
  currency: string;
  totalCount: number;
  totalCents: number;
  searchRef?: React.RefObject<HTMLInputElement>;
  exportHref: string;
  isMobile: boolean;
  isAdmin: boolean;
}

/**
 * An amount box that filters as you type.
 *
 * It keeps the raw text while the field has focus — "12." and "12.0" are both on the way
 * to 12.05, and re-formatting them mid-keystroke would eat the decimal point. The parsed
 * value goes out on every keystroke; the tidy formatting comes back on blur.
 */
function AmountInput({
  label,
  cents,
  currency,
  onCommit,
}: {
  label: string;
  cents: number | undefined;
  currency: string;
  onCommit: (value: number | undefined) => void;
}) {
  const formatted = cents === undefined ? "" : formatAmount(cents, currency);
  const [text, setText] = useState(formatted);
  const focused = useRef(false);

  // A preset or "Clear all" can change the filter from outside; follow it when idle.
  useEffect(() => {
    if (!focused.current) setText(formatted);
  }, [formatted]);

  return (
    <label className="min-w-0 flex-1 text-xs text-muted">
      {label}
      <Input
        inputMode="decimal"
        className="h-9"
        value={text}
        onFocus={() => {
          focused.current = true;
        }}
        onChange={(event) => {
          setText(event.target.value);
          onCommit(
            event.target.value.trim() === ""
              ? undefined
              : (parseAmount(event.target.value, currency) ?? undefined),
          );
        }}
        onBlur={() => {
          focused.current = false;
          setText(formatted);
        }}
      />
    </label>
  );
}

/** A labelled group of controls: a popover on desktop, a plain section in the sheet. */
function Group({
  label,
  active,
  count,
  inline,
  wide,
  children,
}: {
  label: string;
  active: boolean;
  count?: number;
  inline: boolean;
  /** Two side-by-side native date boxes need more room than a column of chips. */
  wide?: boolean;
  children: React.ReactNode;
}) {
  if (inline) {
    return (
      <section className="border-t border-rule py-3 first:border-t-0">
        <h3 className="mb-2 text-2xs uppercase tracking-widest text-muted">{label}</h3>
        {children}
      </section>
    );
  }
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            "chip",
            active && "border-accent/50 bg-accent/[0.07] text-accent hover:bg-accent/10",
          )}
        >
          {label}
          {count ? <span className="chip-count">{count}</span> : null}
          <span
            aria-hidden
            className={cn("text-[10px] opacity-60 transition-transform", active && "opacity-100")}
          >
            ▾
          </span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          sideOffset={8}
          align="start"
          collisionPadding={12}
          className={cn(
            "surface z-50 p-3.5 data-[state=open]:animate-scale-in",
            wide ? "w-[min(26rem,calc(100vw-1.5rem))]" : "w-[min(20rem,calc(100vw-1.5rem))]",
          )}
        >
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function Controls({
  filters,
  onChange,
  members,
  categories,
  currency,
  inline,
}: Pick<Props, "filters" | "onChange" | "members" | "categories" | "currency"> & {
  inline: boolean;
}) {
  const toggleIn = (list: number[], id: number) =>
    list.includes(id) ? list.filter((value) => value !== id) : [...list, id];

  return (
    <>
      <Group label="Date" active={Boolean(filters.from || filters.to)} inline={inline} wide>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(
              [
                ["Last week", "last-week"],
                ["This month", "this-month"],
                ["Last 30 days", "last-30"],
                ["This year", "this-year"],
                ["All time", "all"],
              ] as const
            ).map(([label, key]) => (
              <button
                key={key}
                type="button"
                className="chip"
                onClick={() => onChange({ ...filters, ...datePreset(key) })}
              >
                {label}
              </button>
            ))}
          </div>
          {/*
            `min-w-0` is the whole fix for the phone: a native date input reports a wide
            intrinsic size, and a flex child that may not shrink below it pushes the
            filter sheet off the side of the screen.
          */}
          <div className="flex gap-2">
            <label className="min-w-0 flex-1 text-xs text-muted">
              From
              <Input
                type="date"
                className="h-10 px-2"
                value={filters.from ?? ""}
                onChange={(event) =>
                  onChange({ ...filters, from: event.target.value || undefined })
                }
              />
            </label>
            <label className="min-w-0 flex-1 text-xs text-muted">
              To
              <Input
                type="date"
                className="h-10 px-2"
                value={filters.to ?? ""}
                onChange={(event) => onChange({ ...filters, to: event.target.value || undefined })}
              />
            </label>
          </div>
        </div>
      </Group>

      <Group
        label="Type"
        active={filters.types.length > 0}
        count={filters.types.length}
        inline={inline}
      >
        <div className="flex flex-wrap gap-1.5">
          {(Object.keys(TYPE_LABELS) as TxnTypeFilter[]).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() =>
                onChange({
                  ...filters,
                  types: filters.types.includes(type)
                    ? filters.types.filter((value) => value !== type)
                    : [...filters.types, type],
                })
              }
              aria-pressed={filters.types.includes(type)}
              className={cn("chip", filters.types.includes(type) && "chip-on")}
            >
              {TYPE_LABELS[type]}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs leading-relaxed text-muted">
          Expenses are things someone bought. Payments are one person paying another back.
          Adjustments are manual corrections. Transfers move a whole group's balances.
        </p>
      </Group>

      <Group
        label="People"
        active={filters.members.length > 0}
        count={filters.members.length}
        inline={inline}
      >
        <div className="flex flex-col gap-2">
          <div className="flex gap-1 text-xs">
            {(["any", "all"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => onChange({ ...filters, membersMode: mode })}
                className={cn("chip h-8", filters.membersMode === mode && "chip-on")}
              >
                {mode === "any" ? "Any of" : "All of"}
              </button>
            ))}
          </div>
          <p className="text-xs leading-relaxed text-muted">
            {filters.membersMode === "any"
              ? "Any of — an entry shows if at least one of the people you pick is on it. Pick Ann and Bo and you get Ann's solo lunch, Bo's solo taxi, and the dinner they shared."
              : "All of — an entry shows only if every person you pick is on it. Pick Ann and Bo and you get the dinner they shared, but neither solo lunch."}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {members.map((member) => (
              <button
                key={member.id}
                type="button"
                onClick={() => onChange({ ...filters, members: toggleIn(filters.members, member.id) })}
                aria-pressed={filters.members.includes(member.id)}
                className={cn("chip", filters.members.includes(member.id) && "chip-on")}
              >
                {member.display_name}
              </button>
            ))}
          </div>
        </div>
      </Group>

      <Group label="Payer" active={filters.payer !== undefined} inline={inline}>
        <div className="flex flex-wrap gap-1.5">
          {members.map((member) => (
            <button
              key={member.id}
              type="button"
              onClick={() =>
                onChange({ ...filters, payer: filters.payer === member.id ? undefined : member.id })
              }
              aria-pressed={filters.payer === member.id}
              className={cn("chip", filters.payer === member.id && "chip-on")}
            >
              {member.display_name}
            </button>
          ))}
        </div>
      </Group>

      <Group
        label="Category"
        active={filters.categories.length > 0}
        count={filters.categories.length}
        inline={inline}
      >
        <div className="flex flex-wrap gap-1.5">
          {categories.map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() =>
                onChange({ ...filters, categories: toggleIn(filters.categories, category.id) })
              }
              aria-pressed={filters.categories.includes(category.id)}
              className={cn("chip", filters.categories.includes(category.id) && "chip-on")}
            >
              {category.name}
            </button>
          ))}
        </div>
      </Group>

      <Group
        label="Amount"
        active={filters.min !== undefined || filters.max !== undefined}
        inline={inline}
      >
        <div className="flex items-end gap-2">
          <AmountInput
            label="Min"
            cents={filters.min}
            currency={currency}
            onCommit={(min) => onChange({ ...filters, min })}
          />
          <AmountInput
            label="Max"
            cents={filters.max}
            currency={currency}
            onCommit={(max) => onChange({ ...filters, max })}
          />
        </div>
      </Group>
    </>
  );
}

export function FilterBar({
  filters,
  onChange,
  members,
  categories,
  currency,
  totalCount,
  totalCents,
  searchRef,
  exportHref,
  isMobile,
  isAdmin,
}: Props) {
  const count = activeFilterCount(filters);
  const [sheetOpen, setSheetOpen] = useState(false);

  const summary = (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-0.5 text-xs text-muted">
      <span>
        {count > 0 ? (
          <span className="font-medium text-accent">
            {count} filter{count === 1 ? "" : "s"} ·{" "}
          </span>
        ) : null}
        {totalCount.toLocaleString()} {totalCount === 1 ? "row" : "rows"}
      </span>
      <span className="num text-ink/70">{formatAmount(totalCents, currency)}</span>
      {count > 0 ? (
        <button
          type="button"
          className="rounded px-1 py-0.5 text-accent transition-colors hover:bg-accent/10"
          onClick={() => onChange({ ...EMPTY_FILTERS })}
        >
          Clear all
        </button>
      ) : null}
      {/* On a phone, the one-tap filter is more use than a spreadsheet; Stats still exports. */}
      {isMobile ? (
        <button
          type="button"
          className="ml-auto inline-flex min-h-[32px] items-center rounded px-1 text-accent transition-colors hover:bg-accent/10"
          aria-pressed={filters.mine}
          onClick={() => onChange({ ...filters, mine: !filters.mine })}
        >
          {filters.mine ? "Show everyone's" : "Show only mine"}
        </button>
      ) : (
        <a
          className="ml-auto inline-flex min-h-[32px] items-center rounded px-1 text-accent transition-colors hover:bg-accent/10"
          href={exportHref}
        >
          Export CSV
        </a>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-2.5 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[9rem] flex-1 sm:max-w-xs">
          <Input
            ref={searchRef}
            type="search"
            placeholder={isMobile ? "Search…" : "Search…  (press /)"}
            aria-label="Search entries"
            value={filters.q ?? ""}
            onChange={(event) => onChange({ ...filters, q: event.target.value || undefined })}
            className="h-10"
          />
        </div>

        {isMobile ? (
          <Dialog.Root open={sheetOpen} onOpenChange={setSheetOpen}>
            <Dialog.Trigger asChild>
              <button type="button" className={cn("chip tap", count > 0 && "chip-on")}>
                Filters{count > 0 ? ` (${count})` : ""}
              </button>
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/20" />
              <Dialog.Content
                className="fixed inset-0 z-50 flex flex-col bg-paper"
                aria-describedby={undefined}
              >
                <div className="flex items-center justify-between border-b border-rule px-4 py-3">
                  <Dialog.Title className="display text-sm">Filters</Dialog.Title>
                  <Dialog.Close asChild>
                    <button type="button" className="tap px-2 text-muted">
                      Done
                    </button>
                  </Dialog.Close>
                </div>
                <div className="flex-1 overflow-y-auto px-4">
                  <Controls
                    filters={filters}
                    onChange={onChange}
                    members={members}
                    categories={categories}
                    currency={currency}
                    inline
                  />
                  <section className="flex flex-wrap gap-1.5 border-t border-rule py-3">
                    <button
                      type="button"
                      onClick={() => onChange({ ...filters, mine: !filters.mine })}
                      aria-pressed={filters.mine}
                      className={cn("chip tap", filters.mine && "chip-on")}
                    >
                      Only entries involving me
                    </button>
                    {isAdmin ? (
                      <button
                        type="button"
                        onClick={() =>
                          onChange({ ...filters, includeDeleted: !filters.includeDeleted })
                        }
                        aria-pressed={filters.includeDeleted}
                        className={cn("chip tap", filters.includeDeleted && "chip-on")}
                      >
                        Show deleted
                      </button>
                    ) : null}
                  </section>
                </div>
                <div className="flex gap-2 border-t border-rule px-4 py-3">
                  <Button className="flex-1" onClick={() => onChange({ ...EMPTY_FILTERS })}>
                    Clear all
                  </Button>
                  <Button
                    variant="primary"
                    className="flex-1"
                    onClick={() => setSheetOpen(false)}
                  >
                    Show {totalCount.toLocaleString()}
                  </Button>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        ) : (
          <>
            <Controls
              filters={filters}
              onChange={onChange}
              members={members}
              categories={categories}
              currency={currency}
              inline={false}
            />
            <button
              type="button"
              onClick={() => onChange({ ...filters, mine: !filters.mine })}
              aria-pressed={filters.mine}
              className={cn("chip tap", filters.mine && "chip-on")}
            >
              Only mine
            </button>
            {isAdmin ? (
              <button
                type="button"
                onClick={() => onChange({ ...filters, includeDeleted: !filters.includeDeleted })}
                aria-pressed={filters.includeDeleted}
                title="Include deleted entries, so they can be restored"
                className={cn("chip tap", filters.includeDeleted && "chip-on")}
              >
                Show deleted
              </button>
            ) : null}
          </>
        )}
      </div>

      {summary}
    </div>
  );
}
